// src/game/roster.ts — Menü seçimi → maç kadrosu (insanlar + botlar) ve oyuncu → dövüşçü eşlemesi.
//
// Neden ayrı ve SAF (DOM/three import etmez): kadro kuralları tek satırlık değil, çok oyuncuya geçince üçe çıktı ve
// üçü de sessizce bozulabilir (yanlış fighterId → HUD başkasını "sen" gösterir; taşan kadro → createWorld atar):
//   1) İNSANLAR ÖNCE gelir → dövüşçü kimliği = kadro sırası, oyuncu i'nin fighterId'si baştan bilinir ve sabittir.
//   2) İnsan + bot ≤ MAX_FIGHTERS (8). Menü bot sayacını zaten kırpıyor (menu.ts botRange), burada İKİNCİ kez kırpılır:
//      menüyü atlayan yollar (?players=N, ?tutorial=1, duman testi) da sınırın içinde kalsın.
//   3) Bot karakterleri insanların seçtiklerinin DIŞINDAN rastgele; havuz biterse baştan doldurulur (11 karakter, 8 yuva).
// Eğitim tek kişiliktir (docs/TUTORIAL.md): oyuncu + Manken kukla, bot yok — buradaki dallanma o sözü koruyor.
import type { Difficulty } from '../core/types.ts';
import { hashNoise } from '../core/math.ts';
import { MAX_FIGHTERS } from '../config/constants.ts';
import { CHARACTERS, MANNEQUIN_IDX } from '../config/characters.ts';
import { MAX_LOCAL_PLAYERS, type LocalPlayer } from '../config/players.ts';
// Tip-only: roster.ts çalışma zamanında game.ts'i (ve dolayısıyla three.js'i) ÇEKMEZ — testler bu dosyayı düz Node'da alır.
import type { GameFighterSpec } from './game.ts';

/** Kadro kurmak için menüden gelen her şey (MenuChoice'ın alt kümesi; menü tipine bağımlı olmayalım). */
export interface RosterChoice {
  /** Yerel oyuncular, index sırasında (P1 ilk). Boş → izleyici/duman testi. */
  players: readonly LocalPlayer[];
  bots: number;
  difficulty: Difficulty;
  mode: 'fight' | 'tutorial';
}

export interface Roster {
  /** world.fighters sırası: önce insanlar (oyuncu sırasında), sonra botlar. */
  fighters: GameFighterSpec[];
  /** Aynı oyuncular, `fighterId` doldurulmuş hâlde (Game bunu olduğu gibi alır). */
  players: LocalPlayer[];
}

/** Menüsüz yollar için tek kişilik varsayılan oyuncu (klavye, pad yok). */
export const soloPlayer = (charIdx: number): LocalPlayer => ({ index: 0, fighterId: -1, padSlot: -1, charIdx, ready: true });

/** Karakter indeksini geçerli aralığa kelepçeler (menü dışından gelen değerler: ?char, kaydedilmiş seçim). */
const clampChar = (i: number): number => Math.max(0, Math.min(CHARACTERS.length - 1, i | 0));

/**
 * Kadro kurmak için deterministik rastgelelik: aynı seed → aynı bot karakterleri, HER MAKİNEDE.
 * NEDEN: ağ maçında iki uç aynı `RosterChoice`tan FARKLI bot karakteri üretirse `charIdx` → `weightMult` farklı olur
 * (`world.ts` `createFighter` ağırlığı karakterden alır) ve dünya daha İLK TICK'te ayrışır (NETWORK.md §2.4).
 * Eskiden burada `Math.random` varsayılanı vardı ve tek üretim çağrısı (`main.ts`) tohum enjekte etmiyordu.
 * Sim'in kendi rastgeleliği `hashNoise(seed, tick, tuz)`; bu sayaç tick akışının DIŞINDA, yalnız kurulumda döner.
 */
export function rosterRand(seed: number): () => number {
  let n = 0;
  return () => hashNoise(seed, n++, ROSTER_SALT);
}
/** Kadro tuzu: aynı seed sim'de de kullanılıyor, kadro çekilişi onunla çakışmasın. */
const ROSTER_SALT = 0x9051e5;

/**
 * Kadro + oyuncu→dövüşçü eşlemesi. `rand` ZORUNLU: varsayılan `Math.random` olsaydı bir çağrının tohum vermeyi
 * unutması sessizce ağ desync'ine dönüşürdü (tam olarak eskiden olan buydu). `rosterRand(seed)` kullan.
 */
export function buildRoster(choice: RosterChoice, rand: () => number): Roster {
  const humans = choice.players.slice(0, MAX_LOCAL_PLAYERS).map((p, i) => ({ ...p, charIdx: clampChar(p.charIdx), fighterId: i }));
  // Eğitim: kadro sabit (oyuncu + Manken). İkinci bir insan gelse bile alınmaz — ders kuralları tek oyuncuya yazılı.
  if (choice.mode === 'tutorial') {
    const p1 = humans[0] ?? { ...soloPlayer(0), fighterId: 0 };
    return {
      fighters: [
        { charIdx: p1.charIdx, isBot: false, difficulty: 'easy' },
        { charIdx: MANNEQUIN_IDX, isBot: true, difficulty: 'easy' },
      ],
      players: [{ ...p1, fighterId: 0 }],
    };
  }
  const fighters: GameFighterSpec[] = humans.map((p) => ({ charIdx: p.charIdx, isBot: false, difficulty: choice.difficulty }));
  // Rakipsiz maç anlamsız: tek insan kaldıysa en az bir bot (menü de aynı alt sınırı gösteriyor — menu.ts botRange).
  const min = humans.length >= 2 ? 0 : 1;
  const bots = Math.max(min, Math.min(choice.bots | 0, MAX_FIGHTERS - fighters.length));
  const taken = new Set(humans.map((p) => p.charIdx));
  const pool: number[] = [];
  for (let i = 0; i < CHARACTERS.length; i++) if (!taken.has(i)) pool.push(i);
  for (let b = 0; b < bots; b++) {
    if (pool.length === 0) for (let i = 0; i < CHARACTERS.length; i++) pool.push(i);
    const k = Math.floor(rand() * pool.length);
    fighters.push({ charIdx: pool.splice(k, 1)[0] ?? 0, isBot: true, difficulty: choice.difficulty });
  }
  return { fighters, players: humans };
}

/**
 * `?players=N` hızlı testi: menüyü/pad katılımını beklemeden N insan YUVASI kurar (bot değil!). P1 klavye + 1. pad,
 * P2–P4 yalnız kendi pad'i; pad yoksa o oyuncunun girdisi NÖTR kalır (tarayıcı testinde sanal pad enjekte edilir).
 * Karakterler ardışık seçilir ki HUD'da kim kim ayırt edilsin.
 */
export function testPlayers(n: number, baseChar = 0): LocalPlayer[] {
  const count = Math.max(1, Math.min(MAX_LOCAL_PLAYERS, n | 0));
  const out: LocalPlayer[] = [];
  for (let i = 0; i < count; i++) {
    out.push({ index: i, fighterId: -1, padSlot: i, charIdx: (clampChar(baseChar) + i) % CHARACTERS.length, ready: true });
  }
  return out;
}
